import { Badge } from '@/components/ui/badge';
import type { EpicRequestDetail } from '@/lib/api/user-activity-details';
import { formatDistanceToNow } from 'date-fns';

interface EpicRequestDetailListProps {
  items: EpicRequestDetail[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-900/30 text-amber-300',
  approved: 'bg-green-900/30 text-green-300',
  rejected: 'bg-red-900/30 text-red-300',
  converted: 'bg-blue-900/30 text-blue-300',
};

export function EpicRequestDetailList({ items }: EpicRequestDetailListProps) {
  return (
    <ul className="space-y-0" role="list">
      {items.map((item) => (
        <li key={item.id}>
          <EpicRequestDetailItem item={item} />
        </li>
      ))}
    </ul>
  );
}

function EpicRequestDetailItem({ item }: { item: EpicRequestDetail }) {
  const statusLabel = item.status.charAt(0).toUpperCase() + item.status.slice(1);
  const ariaLabel = `Epic Request ${item.identifier}: ${item.title}. Status: ${statusLabel}. ${item.teamName ? `Team: ${item.teamName}.` : 'Personal scope.'} ${item.requestedByName ? `Requested by ${item.requestedByName}.` : ''} Created ${formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}`;

  return (
    <div
      className="border-b py-3 px-3 sm:px-4 hover:bg-muted/50 transition-colors"
      aria-label={ariaLabel}
      role="article"
    >
      <div className="space-y-2">
        <div className="flex items-start sm:items-center gap-2 flex-col sm:flex-row">
          <Badge
            className="text-xs font-mono bg-cyan-900/30 text-cyan-300 px-2 rounded border-0 flex-shrink-0"
            aria-hidden="true"
          >
            {item.identifier}
          </Badge>
          <span className="font-medium text-sm sm:text-base line-clamp-2 sm:line-clamp-1 sm:truncate">{item.title}</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap" aria-hidden="true">
          <Badge
            className={`text-xs px-1.5 rounded border-0 ${STATUS_COLORS[item.status] ?? 'bg-gray-900/30 text-gray-300'}`}
          >
            {statusLabel}
          </Badge>
          <span className="hidden sm:inline">•</span>
          <span className="truncate max-w-[120px] sm:max-w-none">{item.teamName ?? 'Personal'}</span>
          {item.requestedByName && (
            <>
              <span className="hidden sm:inline">•</span>
              <span className="truncate max-w-[100px] sm:max-w-none">{item.requestedByName}</span>
            </>
          )}
          <span className="hidden sm:inline">•</span>
          <span className="whitespace-nowrap">{formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}</span>
        </div>
      </div>
    </div>
  );
}
